import { supabase } from '../../shared/services/supabase';
import { CustomerCustomAttachment } from '../../shared/types';

export class CustomerAttachmentService {
  /**
   * Lädt alle aktiven Attachments für einen Kunden
   */
  async getCustomerAttachments(customerId: string): Promise<CustomerCustomAttachment[]> {
    try {
      const { data, error } = await supabase
        .from('customer_custom_attachments')
        .select('*')
        .eq('customer_id', customerId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error loading customer attachments:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getCustomerAttachments:', error);
      return [];
    }
  }

  /**
   * Erstellt ein neues Attachment für einen Kunden
   */
  async createAttachment(
    customerId: string, 
    filename: string, 
    fileType: CustomerCustomAttachment['file_type'],
    description?: string,
    isRequired: boolean = true
  ): Promise<CustomerCustomAttachment | null> {
    try {
      // Hole die höchste sort_order für diesen Kunden
      const { data: maxOrderData } = await supabase
        .from('customer_custom_attachments')
        .select('sort_order')
        .eq('customer_id', customerId)
        .order('sort_order', { ascending: false })
        .limit(1);

      const nextSortOrder = maxOrderData && maxOrderData.length > 0 
        ? (maxOrderData[0].sort_order || 0) + 1 
        : 0;

      const { data, error } = await supabase
        .from('customer_custom_attachments')
        .insert([{
          customer_id: customerId,
          filename: filename.trim(),
          file_type: fileType,
          description: description?.trim() || null,
          is_required: isRequired,
          is_active: true,
          sort_order: nextSortOrder
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating attachment:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in createAttachment:', error);
      return null;
    }
  }

  /**
   * Aktualisiert ein Attachment
   */
  async updateAttachment(
    attachmentId: string, 
    updates: Partial<Pick<CustomerCustomAttachment, 'filename' | 'description' | 'file_type' | 'is_required'>>
  ): Promise<boolean> {
    try {
      const updateData: any = {};
      
      if (updates.filename !== undefined) {
        updateData.filename = updates.filename.trim();
      }
      if (updates.description !== undefined) {
        updateData.description = updates.description.trim() || null;
      }
      if (updates.file_type !== undefined) {
        updateData.file_type = updates.file_type;
      }
      if (updates.is_required !== undefined) {
        updateData.is_required = updates.is_required;
      }

      const { error } = await supabase
        .from('customer_custom_attachments')
        .update(updateData)
        .eq('id', attachmentId);

      if (error) {
        console.error('Error updating attachment:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateAttachment:', error);
      return false;
    }
  }

  /**
   * Löscht ein Attachment (soft delete - setzt is_active auf false)
   */
  async deleteAttachment(attachmentId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('customer_custom_attachments')
        .update({ is_active: false })
        .eq('id', attachmentId);

      if (error) {
        console.error('Error deleting attachment:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteAttachment:', error);
      return false;
    }
  }

  /**
   * Ändert die Reihenfolge der Attachments
   */
  async reorderAttachments(customerId: string, attachmentIds: string[]): Promise<boolean> {
    try {
      // Update alle Attachments mit neuer sort_order
      const updates = attachmentIds.map((attachmentId, index) => 
        supabase
          .from('customer_custom_attachments')
          .update({ sort_order: index })
          .eq('id', attachmentId)
          .eq('customer_id', customerId)
      );

      const results = await Promise.all(updates);
      
      // Prüfe ob alle Updates erfolgreich waren
      const hasError = results.some(result => result.error);
      if (hasError) {
        console.error('Error reordering attachments');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in reorderAttachments:', error);
      return false;
    }
  }

  /**
   * Lädt Attachments für mehrere Kunden (für Batch-Loading)
   */
  async getAttachmentsForCustomers(customerIds: string[]): Promise<Record<string, CustomerCustomAttachment[]>> {
    try {
      if (customerIds.length === 0) return {};

      const { data, error } = await supabase
        .from('customer_custom_attachments')
        .select('*')
        .in('customer_id', customerIds)
        .eq('is_active', true)
        .order('customer_id', { ascending: true })
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error loading attachments for customers:', error);
        return {};
      }

      // Gruppiere Attachments nach customer_id
      const groupedAttachments: Record<string, CustomerCustomAttachment[]> = {};
      (data || []).forEach(attachment => {
        if (!groupedAttachments[attachment.customer_id]) {
          groupedAttachments[attachment.customer_id] = [];
        }
        groupedAttachments[attachment.customer_id].push(attachment);
      });

      return groupedAttachments;
    } catch (error) {
      console.error('Error in getAttachmentsForCustomers:', error);
      return {};
    }
  }

  /**
   * Validiert Dateiformat basierend auf erlaubten Typen - ZIP-Unterstützung hinzugefügt
   */
  validateFileType(file: File, allowedTypes: CustomerCustomAttachment['file_type'][]): boolean {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    const typeMapping: Record<string, CustomerCustomAttachment['file_type']> = {
      'pdf': 'pdf',
      'doc': 'doc',
      'docx': 'docx',
      'xlsx': 'xlsx',
      'xls': 'xls',
      'txt': 'txt',
      'jpg': 'jpg',
      'jpeg': 'jpeg',
      'png': 'png',
      'xml': 'xml',
      'zip': 'zip'
    };

    const detectedType = typeMapping[fileExtension || ''];
    return detectedType ? allowedTypes.includes(detectedType) : false;
  }

  /**
   * Konvertiert File zu Base64 für E-Mail-Anhang
   */
  async fileToBase64(file: File): Promise<{ filename: string; content: string; contentType: string }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result as string;
        // Remove data:*/*;base64, prefix
        const base64Content = base64.split(',')[1];
        resolve({
          filename: file.name,
          content: base64Content,
          contentType: file.type || 'application/octet-stream'
        });
      };
      reader.onerror = error => reject(error);
    });
  }
}

// Singleton instance
export const customerAttachmentService = new CustomerAttachmentService();